/**
 * @file utils/widgets/library.js
 * @copyright 2017 CBC/Radio-Canada. All rights reserved.
 * This file contains methods needed to fetch/display widgets on the site, e.g. the sidebar
 */
import SponsorInfo from "../../components/SponsorInfo/component";
import Series from "../../components/Series/component";
import BusinessWidget from "../../components/BusinessWidget/component";
import ScheduleRightRail from "../../components/Schedule/components/ScheduleRightRail/component";
import RegionInfo from "../../components/RegionInfo/component";
import { BARCHART_BASE_URL } from "../../constants/widgets";
import { SHOWS } from "../../constants/shows";
import { PAGE_WIDGETS, SIDEBAR_WIDGETS } from "../../constants/widgets";
import { fetchFromObject } from "../dataTransform/utils";
import { SECTIONS } from "../../constants/sections";
import ImageLinkWidget from "../../components/ImageLinkWidget/component";
import ImageButtonWidget from "../../components/ImageButtonWidget/component";
import SecureDrop from "../../components/SecureDropWidget";
import { TrendingListSidebar } from "../../components/TrendingList";
import VerticalCardList from "../../components/VerticalCardList/component";
import NewsletterWidget from "../../components/NewsletterWidget/component";
import ContestPromoContainer from "../../pages/FlexibleLandingPage/components/ContestPromoContainer/component";
import { MyLocalContainer } from "../../pages/FlexibleLandingPage/components/MyLocalContainer/component";
import Ad from "../../components/Ad/component";
import CustomHtml from "../../components/CustomHtml/component";
import ErrorBoundary from "../../components/ErrorBoundary/component";
import VideoRecommendations from "../../components/VideoRecommendations/component";
import CTA from "../../components/CTA/component";
import log from "../../utils/logger/library";
import {
    TYPE_SPONSORED,
    TYPE_ADELEMENT,
    TYPE_MYLOCAL,
    TYPE_CUSTOMHTML,
    TYPE_NEWSLETTER,
    TYPE_CONTESTPROMO,
    TYPE_LINEUP,
    TYPE_RECOMMENDED_VIDEO,
    TYPE_CTA,
    TYPE_BROADCAST_SCHEDULE,
} from "../../constants/page";
import { DISPLAY_TYPES } from "../../components/ContentList/constants";
import { LINK_VARIANT } from "../../components/CTA/constants";

/**
 * Get name of all widgets for path
 */
export const getWidgetNames = (path) => {
    return PAGE_WIDGETS[path] || [];
};

/**
 * Get definitions of all widgets for given names
 * @param {Array<string>} names
 * @return {Array<WidgetDef>}
 *
 * @type WidgetDef
 * @param {string} type
 */
export const getWidgetDefs = (names = []) => {
    return names.map((name) => SIDEBAR_WIDGETS[name]);
};

export const getSidebarWidgets = ({
    path,
    rightrail,
    viewport,
    baseSection,
    sectionalContent,
    contentId,
}) => {
    if (path === undefined) {
        return;
    }

    // Array of widgets per page, example: news: ["widget1","widget2"]
    const widgetList = [];
    const pageWidgets = getWidgetNames(path);

    // List of Polopoly Widgets to be inserted before the constant PAGE_WIDGETS
    if (
        rightrail &&
        path === rightrail.path &&
        rightrail.widgets &&
        rightrail.widgets.length > 0
    ) {
        rightrail.widgets.forEach((widget, index) => {
            switch (widget.type) {
                case TYPE_LINEUP:
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <VerticalCardList
                                title={widget.wrapper?.heading?.title}
                                hideTitle={!widget.wrapper?.heading?.visible}
                                items={widget.content}
                                featureInstance={
                                    widget.wrapper?.attribution?.featureInstance
                                }
                            />
                        </ErrorBoundary>
                    );
                    break;
                case TYPE_SPONSORED:
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <SponsorInfo
                                sponsorContent={widget}
                                rightRail={true}
                            />
                        </ErrorBoundary>
                    );
                    break;
                case TYPE_ADELEMENT: {
                    widgetList.push(
                        <Ad
                            key={`${index}-${widget.type}`}
                            type={widget.adType}
                            targeting={{ plc: "feed" }}
                            lazy={
                                viewport === "viewportSmall" &&
                                path === "homepage"
                            }
                        />
                    );
                    break;
                }
                case TYPE_CTA:
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <CTA
                                text={widget.title}
                                description={widget.description ?? ""}
                                linkText={widget.linkText ?? ""}
                                linkUrl={widget.linkUrl}
                                external={/^(http).*/.test(widget.linkUrl)}
                                flexPage={false}
                                titleVisible={widget.titleVisible}
                                image={widget.image}
                                variant={LINK_VARIANT}
                            />
                        </ErrorBoundary>
                    );
                    break;
                case TYPE_MYLOCAL:
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <MyLocalContainer
                                {...{
                                    params: {
                                        displayFilter: widget.displayFilter,
                                    },
                                }}
                            />
                        </ErrorBoundary>
                    );
                    break;

                case TYPE_NEWSLETTER: {
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <NewsletterWidget
                                promotenewsletter={widget.newsletter}
                            />
                        </ErrorBoundary>
                    );
                    break;
                }

                case TYPE_CUSTOMHTML: {
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <CustomHtml
                                html={widget.htmlContent}
                                targetParent={true}
                            />
                        </ErrorBoundary>
                    );
                    break;
                }
                case TYPE_CONTESTPROMO: {
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <ContestPromoContainer params={widget} />
                        </ErrorBoundary>
                    );
                    break;
                }
                case TYPE_RECOMMENDED_VIDEO: {
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <VideoRecommendations />
                        </ErrorBoundary>
                    );
                    break;
                }
                case TYPE_BROADCAST_SCHEDULE: {
                    let scheduleConfig;
                    try {
                        scheduleConfig = JSON.parse(
                            widget.broadcastschedule?.options || "{}"
                        );
                    } catch (e) {
                        log.error(
                            "(getSidebarWidgets) TYPE_BROADCAST_SCHEDULE",
                            e
                        );
                        scheduleConfig = {};
                    }
                    const scheduleProps = {
                        type: "sports",
                        scheduleConfig,
                        ...(widget.title ? { headline: widget.title } : {}),
                    };
                    widgetList.push(
                        <ErrorBoundary key={`${index}-${widget.type}`}>
                            <div className="scheduleWrapper">
                                <ScheduleRightRail {...scheduleProps} />
                            </div>
                        </ErrorBoundary>
                    );
                    break;
                }
                default:
                    break;
            }
        });
    }

    // List of manually defined widgets in PAGE_WIDGETS appear below Polopoly widget
    if (pageWidgets !== undefined) {
        const section = baseSection;
        const sidebarWidgets = SIDEBAR_WIDGETS;

        // Every widget configuration
        const widgets = Object.assign(
            {},
            {
                series: { series: SHOWS },
            }
        );

        // Check if there are Sponsors defined for this section
        pageWidgets.forEach((item, index) => {
            //Get the widget info from config
            let seriesItem = fetchFromObject(widgets.series, item);
            let widgetItem = sidebarWidgets[item];
            const widgetSeries =
                widgets &&
                widgets.series &&
                widgets.series.series &&
                widgets.series.series[section]
                    ? widgets.series.series[section]
                    : null;

            if (widgetItem && widgetItem.type === "imageLink") {
                widgetList.push(
                    <ErrorBoundary key={index}>
                        <ImageLinkWidget {...widgetItem} />
                    </ErrorBoundary>
                );
            } else if (widgetItem && widgetItem.type === "imageButton") {
                widgetList.push(
                    <ErrorBoundary key={index}>
                        <ImageButtonWidget {...widgetItem} />
                    </ErrorBoundary>
                );
            } else if (widgetItem && widgetItem.type === "secureDrop") {
                widgetList.push(
                    <ErrorBoundary key={index}>
                        <SecureDrop {...widgetItem} />
                    </ErrorBoundary>
                );
            } else if (
                widgetItem &&
                (widgetItem.type === "business" || widgetItem.type === "form")
            ) {
                //Test if widgetItem.src starts with http(s)
                let re = new RegExp("^(http|https)://", "i");
                let match = re.test(widgetItem.src);
                let businessWidgetUrl = widgetItem.src;
                // Construct the URL if doesn't have http(s)
                if (!match) {
                    businessWidgetUrl = BARCHART_BASE_URL + widgetItem.src;
                }
                widgetList.push(
                    <ErrorBoundary key={`businesswidget${index}`}>
                        <BusinessWidget
                            src={businessWidgetUrl}
                            name={widgetItem.name}
                            height={widgetItem.height}
                            trackingSrc={widgetItem.trackingSrc}
                        />
                    </ErrorBoundary>
                );
            } else if (widgetItem && widgetItem.type === "region") {
                // If in the future there is more than one kind of region widget
                // it would be good to pass a name property to be applied as a css class
                // on the top level wrapper of the widget for easy targetting
                widgetList.push(
                    <ErrorBoundary key={`regionWidget${index}`}>
                        <RegionInfo isSwimlane={false} />
                    </ErrorBoundary>
                );
            } else if (widgetItem && widgetItem.type === "schedule") {
                widgetList.push(
                    <ErrorBoundary key="sportsSchedule">
                        <ScheduleRightRail
                            type="sports"
                            scheduleConfig={widgetItem}
                        />
                    </ErrorBoundary>
                );
            } else if (seriesItem) {
                // Check if there are shows defined for this section and whether they should appear in the sidebar
                if (widgetSeries.length) {
                    widgetList.push(
                        <ErrorBoundary key={`${section}ShowsList`}>
                            <Series
                                items={widgetSeries}
                                baseSection={section}
                            />
                        </ErrorBoundary>
                    );
                }
            } else if (item === "trending") {
                widgetList.push(
                    <ErrorBoundary key={`${section}-TrendingListSidebar`}>
                        <TrendingListSidebar
                            section={section}
                            hideSectionLabel={true}
                            displayType={DISPLAY_TYPES.TYPE_LIST_ORDERED}
                        />
                    </ErrorBoundary>
                );
            } else if (widgetItem && widgetItem.type === "contentlist") {
                // This widget displays a content list in the sidebar, for Community news, Cree etc.
                const path = widgetItem.path ? widgetItem.path : "";
                const contentItems =
                    sectionalContent && sectionalContent[path]
                        ? sectionalContent[path].sectionList
                        : null;
                const listProps = {
                    title: widgetItem.title,
                    items: contentItems,
                    displayPolicy: widgetItem.displayPolicy,
                };

                widgetList.push(
                    <ErrorBoundary key={`${contentId}-ContentList`}>
                        <VerticalCardList {...listProps} />
                    </ErrorBoundary>
                );
            }
        });
    }

    return widgetList;
};

/**
 * Determine if the trending list is configured/enabled for the page
 * @param {object} props Component props
 * @returns {boolean} true if trending is enabled
 */
export const isTrendingEnabled = ({ baseSection, pageType, path }) => {
    if (
        pageType === "show" &&
        path &&
        PAGE_WIDGETS[path] &&
        PAGE_WIDGETS[path].includes("trending")
    ) {
        return true;
    } else if (
        baseSection &&
        SECTIONS[baseSection] &&
        SECTIONS[baseSection].enableTrendingSideBar
    ) {
        return true;
    }
    return false;
};

export const iframeTracking = {
    iframeClickHandler(event) {
        let message = event.data;
        if (
            message &&
            typeof message === "string" &&
            message.startsWith(iframeTracking.messageID)
        ) {
            message = message.slice(iframeTracking.messageID.length);
            try {
                const i = message.indexOf("|");
                const data = message.slice(i + 1);
                const label = message.substr(0, i);
                window.CBC.APP.SC.LinkTracker.registerLinkClick(label, data);
            } catch (error) {
                log.warn(
                    "(iframeTracking) ERROR: CBC.APP.SC.LinkTracker.registerLinkClick",
                    error
                );
            }
        }
    },

    iframeClickHandlerInit() {
        //register only once
        window.addEventListener("message", this.iframeClickHandler, false);
        this.iframeClickHandlerInit = () => {};
    },

    registerIFrameLinkClick(id) {
        //define function for handle <a /> clicks inside an iframe
        const iframeFunc = () => {
            const callback = (e) => {
                if (
                    e.target.tagName !== "A" &&
                    e.target.parentNode.tagName !== "A"
                ) {
                    return;
                }
                const parent = window.parent;
                if (parent && parent.postMessage) {
                    let label =
                        e.target.innerText || e.target.textContent || "";
                    label = label.trim() || e.target.getAttribute("title");
                    parent.postMessage(`%1%${label}`, "%2%");
                }
            };
            if (document.addEventListener) {
                document.body.addEventListener("click", callback, false);
            }
        };
        //init message listener
        this.iframeClickHandlerInit();
        //update function with message identifier, and unique id
        const func = iframeFunc
            .toString()
            .replace("%1%", `${iframeTracking.messageID}${id}|`)
            .replace("%2%", "*");

        //return self executing function string
        return `(${func})();`;
    },
};

iframeTracking.messageID = "tracking-util|";
